import {
  getWorkingProject,
  getWorkingProjectUuid,
  selectIsCourseEnabled,
  selectWorkingProjectType,
} from '@/store/slices/project/selectors';
import SessionFeatures from '../session-features/session-features';
import { getDefaultFeaturesByProjectType } from '../session-features/session-features-helpers';
import getSelectedFeaturesBySession from '@/utils/get-selected-features-by-session';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Session } from '@/types/session.types';
import { EventType } from '@/types/projects.types';
import handleUpdateFeaturesBySession from './update-methods/handle-update-session-features';
import handleUpdateFeaturesByProject from '../edit-project-features/update-methods/handle-update-project-features';
import { useEffect, useState } from 'react';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { ICourse } from '@/types/courses';
import { GetCourseForEvent } from '@/connection/courses';

type TEditSessionFeaturesProps = {
  session?: Session | null;
};

const EditSessionFeatures = ({ session }: TEditSessionFeaturesProps) => {
  const projectType = useAppSelector(selectWorkingProjectType);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const isCourseEnabled = useAppSelector(selectIsCourseEnabled);

  const project = useAppSelector(getWorkingProject);
  const userActiveChannel = useAppSelector(getActiveChannel) || 0;

  const [course, setCourse] = useState<ICourse | null>(null);

  const dispatch = useAppDispatch();

  // need to know if a course exists in order to make it toggleable
  // TODO: should probably move course stuff to redux
  useEffect(() => {
    if (projectUuid && userActiveChannel && projectType === EventType.Webinar) {
      (async () => {
        try {
          const course = await GetCourseForEvent(userActiveChannel, projectUuid);
          setCourse(course);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e: unknown) {
          //swallow
        }
      })();
    }
  }, [userActiveChannel, projectType, projectUuid]);

  if (!session) return null;
  return (
    <SessionFeatures
      projectTypeDefaultFeatures={getDefaultFeaturesByProjectType(projectType)}
      selectedFeatures={getSelectedFeaturesBySession({ projectType, session, isCourseEnabled })}
      onChange={(_updatedFeatures, toggledFeature) => {
        if (projectType === EventType.Event) {
          handleUpdateFeaturesBySession({ session, toggledFeature, dispatch });
          return;
        }
        // I'm still wrapping them in their own if statements so that we can easily distinguish the difference
        // if we need to add more logic in the future
        if (projectType === EventType.Webinar) {
          handleUpdateFeaturesByProject({ project, toggledFeature, dispatch });
          handleUpdateFeaturesBySession({ session, toggledFeature, dispatch });
          return;
        }

        if (projectType === EventType.Townhall || projectType === EventType.Allhands) {
          handleUpdateFeaturesBySession({ session, toggledFeature, dispatch });
          return;
        }
      }}
      hasCourse={!!course}
    />
  );
};

export default EditSessionFeatures;
